import {
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { FC, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Coffee,
  LucideIcon,
  Sandwich,
  Utensils,
} from "lucide-react";
import { useDisplaySupplierMenu } from "@/services/useSupplierBooking";
import { DateTime } from "luxon";
import RichText from "@ui/components/richtext/RichText";

interface MealOptionProps {
  label: string;
  icon: LucideIcon;
  isSelected: boolean;
  isAdhocServed?: boolean;
  register?: any;
  isDisabled?: any;
  date?: DateTime | null;
  isBulkbooking?: boolean;
}

const MealOption: FC<MealOptionProps> = ({
  label,
  icon: Icon,
  isSelected,
  isAdhocServed,
  register,
  isDisabled = false,
  date,
  isBulkbooking,
}) => {
  const iconColor = useColorModeValue("#555f71", "#aaafb8");
  const selectedColor = useColorModeValue("#1F845A", "#7EE2B8");

  const [showMenu, setShowMenu] = useState(false);
  const { data: menuData } = useDisplaySupplierMenu(date);
  const mealMenu = menuData?.find(
    (item: any) => item.meal === label.toLowerCase(),
  )?.menu;
  const adhocColor = useColorModeValue("#DAA520", "#FFD700");

  const borderColor = isSelected
    ? "green.400"
    : isAdhocServed
      ? "yellow.400"
      : "gray.200";

  const bgColor = isSelected
    ? "green.100"
    : isAdhocServed
      ? "yellow.100"
      : "white";

  const iconStroke = isSelected
    ? selectedColor
    : isAdhocServed
      ? adhocColor
      : iconColor;

  const textColor = isSelected
    ? selectedColor
    : isAdhocServed
      ? adhocColor
      : iconColor;

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      boxShadow="sm"
      borderColor={borderColor}
      bg={bgColor}
      minW="full"
      transition="all 0.2s"
      _hover={{
        borderColor: isAdhocServed ? "yellow.500" : "green.500",
        boxShadow: "md",
      }}
      display="flex"
      justifyContent={"space-between"}
      mt={2}
      userSelect={"none"}
    >
      <Box as="label" flex={"1 1 auto"} cursor="pointer" userSelect={"none"}>
        <Checkbox
          {...register}
          value={label}
          isChecked={isSelected}
          isDisabled={isDisabled}
          sx={{
            ".chakra-checkbox__control": {
              display: "none",
            },
          }}
          userSelect={"none"}
        >
          <Flex gap={3} px={4} py={3}>
            <Icon size={20} strokeWidth={2} stroke={iconStroke} />
            <Text fontWeight="medium" color={textColor}>
              {label}
            </Text>
          </Flex>
          <Collapse in={showMenu} animateOpacity>
            <Box bg={isSelected ? "green.100" : "white"} px={4} mb={3}>
              {mealMenu ? (
                <RichText content={mealMenu} />
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No menu set for this meal
                </Text>
              )}
            </Box>
          </Collapse>
        </Checkbox>
      </Box>
      {!isBulkbooking && (
        <>
          <Box flex={"0 0 auto"} w={"80px"} borderLeft={"1px solid #00000022"}>
            <HStack
              height={"45px"}
              justify="center"
              userSelect="none"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowMenu((prev: any) => !prev);
              }}
            >
              <Text fontWeight="medium" color={"#555f71"}>
                Menu
              </Text>
              {showMenu ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </HStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default MealOption;

export const MealDictionary = {
  breakfast: {
    key: 1,
    order: 1,
    position: "top",
    name: "breakfast",
    label: "Breakfast",
    color: "#FFE680",
    bg: "#FDF5E6",
    textColor: "#6B4F1D",
    borderColor: "#F7E0C3",
    icon: Coffee,
  },
  lunch: {
    key: 2,
    order: 2,
    position: "middle",
    name: "lunch",
    label: "Lunch",
    color: "#A8D5E2",
    bg: "#E5F4FA",
    textColor: "#1B5583",
    borderColor: "#A8D5E2",
    icon: Sandwich,
  },
  dinner: {
    key: 3,
    order: 3,
    position: "bottom",
    name: "dinner",
    label: "Dinner",
    color: "#FFA07A",
    bg: "#FFEDE9",
    textColor: "#8B3A2D",
    borderColor: "#FFA07A",
    icon: Utensils,
  },
};
